import { useCallback, useMemo, useState, type FormEvent, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { FolderPlus, Plus } from "lucide-react";
import { space } from "@tokens/index";
import { Button, Surface, TextInput, useViewport } from "@primitives/index";
import { QueueTree, QueueWorkItemsDialog } from "@compositions/index";
import {
    AdminPage,
    AdminPageHeader,
    AdminSection,
    Field,
    FormActions,
    MutationError,
} from "../admin-ui";
import type { QueueSummary } from "../queues";
import { useCreateQueue, useQueueTree, useQueueWorkItemTallies } from "../queues";
import { useWorkItemsByQueue } from "../workitems";
import { collectWorkableQueueIds } from "./helpers";

// The Open/Pending chip the user drilled into, paired with its queue.
// Null = no drill-in active (dialog closed).
interface BucketDrillIn {
    readonly queue: QueueSummary;
    readonly bucket: "open" | "pending";
}

interface QueueBucketDialogProps {
    readonly view: BucketDrillIn | null;
    readonly onClose: () => void;
}

// Owns the bucketed fetch for the drill-in dialog. Always mounted so the
// dialog's open/close (and exit animation) is driven purely by `view`;
// the query disables itself when closed (empty queue id) and re-enables
// against the chosen queue + bucket when a chip is activated. The items
// flow through the shared `["workitems","byQueue"]` cache, so the dialog
// inherits realtime add/remove from QueueRealtimeBridge without any local
// subscription: completed items drop out, newly-created and newly-due
// (Pending -> Open) items appear on the next reconciliation. A snooze that
// moves Open -> Pending is the one transition not patched live; it
// self-heals on the 15s stale refetch (an accepted, infrastructure-free
// tradeoff).
function QueueBucketDialog({ view, onClose }: QueueBucketDialogProps): ReactElement {
    const query = useWorkItemsByQueue({
        queueId: view?.queue.id ?? "",
        view: "active",
        ...(view !== null ? { bucket: view.bucket } : {}),
    });

    return (
        <QueueWorkItemsDialog
            isOpen={view !== null}
            queueName={view?.queue.name ?? ""}
            bucket={view?.bucket ?? "open"}
            items={query.data?.items ?? []}
            totalCount={query.data?.totalCount ?? 0}
            isLoading={query.isLoading}
            error={query.error}
            onClose={onClose}
        />
    );
}

// "Draft mode" expresses the intent the user is currently composing:
//  - root-shared:    a peer queue at the top level
//  - root-category:  a non-workable grouping node at the top level
//  - child:          a queue parented under an existing root
type DraftMode =
    | { readonly kind: "idle" }
    | { readonly kind: "root-shared" }
    | { readonly kind: "root-category" }
    | { readonly kind: "child"; readonly parent: QueueSummary };

interface QueuesIndexProps {
    readonly onNavigate: (queueId: string) => void;
}

function QueuesIndex({ onNavigate }: QueuesIndexProps): ReactElement {
    const { isCompact } = useViewport();
    const treeQuery = useQueueTree();
    const createMutation = useCreateQueue();
    const [draftName, setDraftName] = useState("");
    const [draftMode, setDraftMode] = useState<DraftMode>({ kind: "idle" });

    const handleCreate = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = draftName.trim();
            if (trimmed.length === 0) return;
            if (draftMode.kind === "idle") return;

            const input = (() => {
                switch (draftMode.kind) {
                    case "root-shared":
                        return { name: trimmed };
                    case "root-category":
                        return { name: trimmed, isWorkable: false };
                    case "child":
                        return { name: trimmed, parentQueueId: draftMode.parent.id };
                }
            })();

            createMutation.mutate(input, {
                onSuccess: (created: QueueSummary) => {
                    setDraftName("");
                    setDraftMode({ kind: "idle" });
                    // Workable queues navigate to detail; categories stay
                    // on the index because there's nothing to manage about
                    // them yet beyond renaming/reparenting.
                    if (created.isWorkable) {
                        onNavigate(created.id);
                    }
                },
            });
        },
        [draftName, draftMode, createMutation, onNavigate],
    );

    const cancelDraft = useCallback(() => {
        setDraftName("");
        setDraftMode({ kind: "idle" });
    }, []);

    const beginCreateChild = useCallback((parent: QueueSummary) => {
        setDraftName("");
        setDraftMode({ kind: "child", parent });
    }, []);

    const handleNavigate = useCallback(
        (queue: QueueSummary) => {
            if (queue.isWorkable) {
                onNavigate(queue.id);
            }
        },
        [onNavigate],
    );

    // Wrap in useMemo so the `?? []` fallback doesn't produce a fresh
    // empty array each render — the queueIds useMemo below otherwise
    // recomputes every pass and busts the tally cache key.
    const roots = useMemo(() => treeQuery.data?.roots ?? [], [treeQuery.data?.roots]);

    // Collect every workable queue id under the tree so one tally fetch
    // covers every row at once. Category nodes (non-workable) don't get
    // tally chips, so they're excluded from the request to keep the
    // queryKey tight.
    const queueIds = useMemo(() => collectWorkableQueueIds(roots), [roots]);
    const { tallies: queueTallies } = useQueueWorkItemTallies(queueIds);

    // Drill-in: clicking a queue's Open/Pending chip opens a modal listing
    // exactly the items behind that count.
    const [bucketView, setBucketView] = useState<BucketDrillIn | null>(null);
    const handleTallyClick = useCallback((queue: QueueSummary, bucket: "open" | "pending") => {
        setBucketView({ queue, bucket });
    }, []);
    const closeBucketView = useCallback(() => {
        setBucketView(null);
    }, []);

    const draftLabel = useMemo(() => {
        switch (draftMode.kind) {
            case "idle":
                return null;
            case "root-shared":
                return "New shared queue";
            case "root-category":
                return "New category (not workable)";
            case "child":
                return `New queue under ${draftMode.parent.name}`;
        }
    }, [draftMode]);

    return (
        <>
            <AdminPageHeader
                title="Queues"
                description="Manage shared work queues, organize them into categories, and staff them with people."
            />

            {draftMode.kind === "idle" && (
                <div
                    style={{
                        display: isCompact ? "grid" : "flex",
                        flexWrap: "wrap",
                        gap: space[2],
                    }}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setDraftMode({ kind: "root-shared" })}
                    >
                        <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
                        New queue
                    </Button>
                    <Button
                        variant="ghost"
                        size="md"
                        onClick={() => setDraftMode({ kind: "root-category" })}
                    >
                        <FolderPlus size={14} strokeWidth={2} aria-hidden="true" />
                        New category
                    </Button>
                </div>
            )}
            {draftMode.kind !== "idle" && draftLabel !== null && (
                <Surface
                    elevation={1}
                    borderRadius="lg"
                    padding={isCompact ? 4 : 5}
                    style={{ width: "100%" }}
                >
                    <form onSubmit={handleCreate} style={{ display: "grid", gap: space[3] }}>
                        <Field label={draftLabel}>
                            {(id) => (
                                <TextInput
                                    id={id}
                                    autoFocus
                                    fullWidth
                                    placeholder={
                                        draftMode.kind === "root-category"
                                            ? "Category name"
                                            : "Queue name"
                                    }
                                    value={draftName}
                                    disabled={createMutation.isPending}
                                    onChange={(e) => setDraftName(e.target.value)}
                                />
                            )}
                        </Field>
                        <MutationError error={createMutation.error} />
                        <FormActions
                            primary={
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="md"
                                    disabled={draftName.trim().length === 0}
                                    busy={createMutation.isPending}
                                >
                                    Create
                                </Button>
                            }
                            secondary={
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="md"
                                    onClick={cancelDraft}
                                    disabled={createMutation.isPending}
                                >
                                    Cancel
                                </Button>
                            }
                        />
                    </form>
                </Surface>
            )}

            <AdminSection>
                <QueueTree
                    roots={roots}
                    isLoading={treeQuery.isLoading}
                    error={treeQuery.error}
                    onSelect={handleNavigate}
                    onCreateChild={beginCreateChild}
                    tallies={queueTallies}
                    onTallyClick={handleTallyClick}
                    groupPersonalQueues
                    // `AdminSection` is a `Surface` at elevation 1.
                    surface="surface"
                />
            </AdminSection>

            <QueueBucketDialog view={bucketView} onClose={closeBucketView} />
        </>
    );
}

// Renders through AdminLayout's <Outlet/>, so the page owns only the
// content-width container and the page-specific content.
export function QueuesIndexPage(): ReactElement {
    const navigate = useNavigate();
    const handleNavigateToQueue = useCallback(
        (queueId: string) => void navigate(`/queues/${queueId}`),
        [navigate],
    );
    return (
        <AdminPage>
            <QueuesIndex onNavigate={handleNavigateToQueue} />
        </AdminPage>
    );
}
